import { InitPage } from "../index";

declare var App: any;

export interface StoreInventoryStatisticsProgressChannelData {
  parent_sync_id: number;
  client_id: number;
  message: string;
  state: string;
  progress: number;
  completed_store_count: number;
  total_store_count: number;
  reset_bulk_flags?: boolean;
  store_code?: string;
  store_name?: string;
  finished_at?: string;
}

class StoreInventoryStatisticsProgressUpdate {
  storeInventoryStatisticsProgressUpdateChannel: any;
  root: HTMLElement;
  tbody: HTMLElement;
  progress: HTMLElement;
  progressMessage: HTMLElement;
  progressCount: HTMLElement;
  progressFill: HTMLElement;
  bulkDownloadOkCount: HTMLElement;
  needsAttentionCount: HTMLElement;

  // ActionCable delivery is best-effort and the authoritative bulk_download_ok
  // flags/timestamps are written to the DB asynchronously by a background
  // worker. To avoid the live table drifting from the DB when a per-store
  // broadcast is dropped, we periodically reconcile the table against the
  // freshly-rendered server HTML while a download is active, and once more
  // after it reaches a terminal state.
  reconcileTimer: number | null = null;
  pendingReconcileTimeouts: number[] = [];
  reconcileInProgress = false;
  static RECONCILE_INTERVAL_MS = 20000;

  constructor() {
    this.root = document.getElementById("store-inventory-statistics-root");
    this.tbody = document.getElementById("sis-tbody");
    this.progress = document.getElementById("sis-download-progress");
    this.progressMessage = document.getElementById("sis-download-progress-message");
    this.progressCount = document.getElementById("sis-download-progress-count");
    this.progressFill = document.getElementById("sis-download-progress-fill");
    this.bulkDownloadOkCount = document.getElementById("sis-bulk-download-ok-count");
    this.needsAttentionCount = document.getElementById("sis-needs-attention-count");

    const clientId = this.root && this.root.getAttribute("data-client-id");
    if (!clientId || !this.tbody || !this.progress || !this.progressFill || typeof App === "undefined" || !App.cable) return;

    this.storeInventoryStatisticsProgressUpdateChannel = App.cable.subscriptions.create(
      {
        channel: "Netsuite::StoreInventoryStatisticsProgressChannel",
        client_id: clientId,
        roomId: `netsuite_store_inventory_statistics_progress_${clientId}`
      },
      {
        connected() {
          console.log("Connected to Netsuite Store Inventory Statistics Progress Channel");
        },
        disconnected() {
          console.log("Disconnected from Netsuite Store Inventory Statistics Progress Channel");
        },
        received: (data: StoreInventoryStatisticsProgressChannelData) => this.updateProgress(data)
      }
    );

    // A download may already be running when the page loads (server renders the
    // progress bar without the `is-hidden` class). Start reconciling right away.
    if (!this.progress.classList.contains("is-hidden")) {
      this.startReconcileLoop();
    }
  }

  updateProgress(data: StoreInventoryStatisticsProgressChannelData) {
    console.log(data);

    const completed = parseInt(String(data.completed_store_count), 10) || 0;
    const total = parseInt(String(data.total_store_count), 10) || 0;
    const percent = Math.max(0, Math.min(100, parseInt(String(data.progress), 10) || 0));
    const state = data.state || "in_progress";

    if (data.reset_bulk_flags) {
      this.resetBulkDownloadRows();
      this.updateBulkDownloadOkCount();
      this.updateNeedsAttentionCount();
    }

    this.progress.classList.remove("is-hidden");
    this.progress.setAttribute("aria-valuenow", String(completed));
    this.progress.setAttribute("aria-valuemax", String(total));

    if (this.progressMessage) {
      this.progressMessage.textContent = data.message || this.root.getAttribute("data-in-progress-label") || "Job is in-progress";
    }
    if (this.progressCount) {
      this.progressCount.textContent = `${completed} / ${total} ${this.root.getAttribute("data-store-label") || "Store"}`;
    }

    this.progressFill.classList.remove("failed", "cancelled");
    if (state === "failed") this.progressFill.classList.add("failed");
    if (state === "cancelled") this.progressFill.classList.add("cancelled");
    this.progressFill.style.width = `${percent}%`;

    this.updateCompletedStoreRow(data);
    this.updateBulkDownloadOkCount();
    this.updateNeedsAttentionCount();
    this.updateActionButtons(state);

    const isTerminal = state === "completed" || state === "failed" || state === "cancelled";
    if (isTerminal) {
      this.stopReconcileLoop();
      // The per-store DB flags/timestamps are persisted asynchronously, so give
      // the background worker a moment to catch up before reconciling, then do
      // it once more in case the last write hadn't landed yet.
      this.scheduleReconcile(2500);
      this.scheduleReconcile(8000);
    } else {
      this.startReconcileLoop();
    }
  }

  startReconcileLoop() {
    if (this.reconcileTimer !== null) return;

    this.reconcileTimer = window.setInterval(
      () => this.reconcileFromServer(),
      StoreInventoryStatisticsProgressUpdate.RECONCILE_INTERVAL_MS
    );
  }

  stopReconcileLoop() {
    if (this.reconcileTimer === null) return;

    window.clearInterval(this.reconcileTimer);
    this.reconcileTimer = null;
  }

  scheduleReconcile(delayMs: number) {
    const id = window.setTimeout(() => this.reconcileFromServer(), delayMs);
    this.pendingReconcileTimeouts.push(id);
  }

  // Pull the authoritative per-store rows and counts from the server and merge
  // them into the live table. This corrects any drift caused by dropped
  // websocket messages or stores whose rows didn't exist when the page loaded.
  async reconcileFromServer() {
    if (this.reconcileInProgress) return;
    this.reconcileInProgress = true;

    try {
      const response = await fetch(window.location.href, {
        headers: { "X-Requested-With": "XMLHttpRequest" },
        credentials: "same-origin"
      });
      if (!response.ok) return;

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, "text/html");

      this.mergeServerRows(doc);
      this.swapCount(doc, "sis-bulk-download-ok-count");
      this.swapCount(doc, "sis-needs-attention-count");
      this.applySearchAndFilter();
    } catch (error) {
      console.error("Failed to reconcile store inventory statistics", error);
    } finally {
      this.reconcileInProgress = false;
    }
  }

  mergeServerRows(doc: Document) {
    const serverTbody = doc.getElementById("sis-tbody");
    if (!serverTbody) return;

    const serverRows = Array.prototype.slice.call(serverTbody.querySelectorAll("tr.sis-row")) as HTMLElement[];

    serverRows.forEach((serverRow: HTMLElement) => {
      const storeCode = serverRow.getAttribute("data-store-code");
      if (!storeCode) return;

      const existing = this.tbody.querySelector(
        `tr.sis-row[data-store-code="${this.cssEscape(storeCode)}"]`
      ) as HTMLElement | null;

      if (existing) {
        // Update in place to preserve the current row order/sort.
        Array.prototype.forEach.call(serverRow.attributes, (attr: Attr) => {
          existing.setAttribute(attr.name, attr.value);
        });
        existing.innerHTML = serverRow.innerHTML;
      } else {
        this.tbody.appendChild(document.importNode(serverRow, true));
      }
    });
  }

  swapCount(doc: Document, id: string) {
    const incoming = doc.getElementById(id);
    const current = document.getElementById(id);
    if (incoming && current) current.textContent = incoming.textContent;
  }

  cssEscape(value: string): string {
    const cssApi = (window as any).CSS;
    if (cssApi && typeof cssApi.escape === "function") return cssApi.escape(value);

    return value.replace(/["\\]/g, "\\$&");
  }

  updateCompletedStoreRow(data: StoreInventoryStatisticsProgressChannelData) {
    if (!data.store_code) return;

    const rows = Array.prototype.slice.call(this.tbody.querySelectorAll("tr.sis-row"));
    const row = rows.find((tr: HTMLElement) => tr.getAttribute("data-store-code") === String(data.store_code));
    if (!row) return;

    row.setAttribute("data-bulk-ok", "true");
    row.setAttribute(
      "data-needs-attention",
      row.getAttribute("data-manual-ok") === "true" && row.getAttribute("data-sync-ok") === "true" ? "false" : "true"
    );

    const cell = row.querySelector(".sis-cell-bulk-download");
    if (!cell) return;

    const timestamp = this.formatTimestamp(data.finished_at);
    const successfulLabel = this.root.getAttribute("data-successful-label") || "Successful";
    cell.innerHTML = `<span class="lbl ok"><i class="fa fa-check-circle"></i> ${successfulLabel}</span>${
      timestamp ? `<div class="sub">${timestamp}</div>` : ""
    }`;

    this.applySearchAndFilter();
  }

  updateActionButtons(state: string) {
    const runOpenBtn = document.getElementById("sis-run-open-btn") as HTMLButtonElement;
    const stopBtn = document.getElementById("sis-stop-btn") as HTMLButtonElement;
    const isTerminal = state === "completed" || state === "failed" || state === "cancelled";

    if (runOpenBtn) runOpenBtn.disabled = !isTerminal;
    if (stopBtn) stopBtn.disabled = isTerminal;
  }

  updateBulkDownloadOkCount() {
    if (!this.bulkDownloadOkCount) return;

    const count = this.tbody.querySelectorAll('tr.sis-row[data-bulk-ok="true"]').length;
    this.bulkDownloadOkCount.textContent = String(count);
  }

  updateNeedsAttentionCount() {
    if (!this.needsAttentionCount) return;

    const count = this.tbody.querySelectorAll(
      'tr.sis-row[data-eligible="true"][data-needs-attention="true"]'
    ).length;
    this.needsAttentionCount.textContent = String(count);
  }

  resetBulkDownloadRows() {
    const rows = this.tbody.querySelectorAll("tr.sis-row");

    rows.forEach((row: HTMLElement) => {
      row.setAttribute("data-bulk-ok", "false");
      row.setAttribute("data-needs-attention", "true");

      const cell = row.querySelector(".sis-cell-bulk-download");
      if (!cell) return;

      cell.innerHTML = `<span class="sis-muted">—</span>`;
    });
  }

  applySearchAndFilter() {
    const search = document.getElementById("sis-search") as HTMLInputElement;
    const filter = document.getElementById("sis-filter") as HTMLSelectElement;
    if (!search || !filter) return;

    const query = (search.value || "").trim().toLowerCase();
    const filterValue = filter.value;
    const rows = this.tbody.querySelectorAll("tr.sis-row");

    rows.forEach((tr: HTMLElement) => {
      const haystack = tr.getAttribute("data-search") || "";
      const matchesText = !query || haystack.indexOf(query) !== -1;
      const matchesFilter = this.rowMatchesFilter(tr, filterValue);
      tr.style.display = matchesText && matchesFilter ? "" : "none";
    });
  }

  rowMatchesFilter(tr: HTMLElement, filter: string): boolean {
    if (filter === "all") return true;
    if (filter === "needs_attention") return tr.getAttribute("data-needs-attention") === "true";
    if (filter === "bulk_ok") return tr.getAttribute("data-bulk-ok") === "true";
    if (filter === "manual_ok") return tr.getAttribute("data-manual-ok") === "true";
    if (filter === "sync_ok") return tr.getAttribute("data-sync-ok") === "true";

    return true;
  }

  formatTimestamp(value: string): string {
    if (!value) return "";

    const parsed = new Date(value);
    if (isNaN(parsed.getTime())) return "";

    const timeZone = this.root.getAttribute("data-time-zone");
    const options: Intl.DateTimeFormatOptions = {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true
    };
    if (timeZone) options.timeZone = timeZone;

    return parsed.toLocaleString("en-US", options).replace(",", "");
  }
}

export function LoadPage() {
  InitPage("lightspeed store_inventory_statistics", () => {
    const storeInventoryStatisticsProgressUpdate = new StoreInventoryStatisticsProgressUpdate();
  });
}
